import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import Router from 'next/router';
import { connect } from 'react-redux';
import useBugsnagNotifier from '../../../../hooks/useBugsnagNotifier';
import SectionList from '../../../SectionList';
import { withEndlessScroll } from '../../../HOC/endlessScroll';
import { ItemCard } from '../../../ui/ItemCard';
import { SpaceSection } from '../../SpaceSection';
import ErrorIcon from '../../../../public/static/images/search/icons/icon-error.svg';
import { FallbackView } from '../../../FallbackView';
import { ItemCardPlaceholder } from '../../../Placeholders/ItemCardPlaceholder';
import { SectionPlaceholder } from '../../../Placeholders/SectionPlaceholder';
import { useBookmarks } from '../../../../hooks/useBookmarks';
import { similarItemsQuery } from '../../../../api/queries';
import { setError } from '../../../../actions';
import { SimilarItemsQuery } from '../../../../graphql/graphql';
import {
  SimilarAdsContainer,
  SimilarAdsContent,
  SimilarAdsList,
  SimilarAdsTitle
} from './styled';
import { getInitialSimilarItemsQueryVariables } from '../../../../utils/helpers/item';
import { CompanyTargetTypeEnum } from '../../../../graphql/types';

const EndlessFeaturedItems = withEndlessScroll(SectionList);
const similarListRef = React.createRef();

const mapDispatchToProps = dispatch => {
  return {
    setErrorUI: (errorState: boolean) => dispatch(setError(errorState))
  };
};

interface SimilarAdsSectionProps {
  categoryId: string;
  leased?: boolean | null;
  currentItemId: string;
  companyId?: string;
  isResidence: boolean;
  categoryPluralName: string | undefined;
  setErrorUI: (value: boolean) => void;
  companyType: CompanyTargetTypeEnum | null;
}

const SimilarAdsSectionComponent: React.FC<SimilarAdsSectionProps> = ({
  categoryId,
  leased,
  currentItemId,
  companyId,
  isResidence,
  setErrorUI,
  categoryPluralName,
  companyType
}) => {
  const { t } = useTranslation();
  const { isBookmarked } = useBookmarks();

  const similarItemsQueryVariables = getInitialSimilarItemsQueryVariables(
    categoryId,
    companyId,
    leased,
    companyType,
    currentItemId
  );

  const { loading, data, error, fetchMore } = useQuery<SimilarItemsQuery>(
    similarItemsQuery,
    {
      variables: similarItemsQueryVariables,
      notifyOnNetworkStatusChange: true
    }
  );

  useEffect(() => {
    if (error) setErrorUI(true);
  }, [error]);

  const similarTitle = (() => {
    if (isResidence) {
      return t('pages.current_item.similar_for_residence');
    }
    if (!currentItemId) {
      return t('pages.current_item.similar_for_nonexistent_ads');
    }
    return t('pages.current_item.similar', {
      type: categoryPluralName?.toLowerCase()
    });
  })();

  const itemsList =
    data?.itemsConnection?.edges?.map(item => {
      return (
        <ItemCard
          key={item?.node?.id}
          {...item?.node}
          inBookmarks={isBookmarked(item?.node?.id)}
        />
      );
    }) || [];

  useBugsnagNotifier(error);

  if (loading && !data) {
    return (
      <SectionPlaceholder>
        {Array.from({ length: 4 }, (_, index) => (
          <ItemCardPlaceholder key={index} isCardsLayout />
        ))}
      </SectionPlaceholder>
    );
  }

  if (!data?.itemsConnection.totalCount) return null;

  return (
    <SimilarAdsContainer>
      <SimilarAdsContent>
        <SpaceSection>
          <SimilarAdsTitle tag="h3">{similarTitle}</SimilarAdsTitle>

          {error ? (
            <FallbackView
              Icon={ErrorIcon}
              width="50"
              height="50"
              text={t('search.error.text')}
              title={t('search.error.title')}
              btnText={t('search.error.link')}
              onClickHandler={() => Router.reload()}
            />
          ) : (
            <SimilarAdsList>
              <EndlessFeaturedItems
                items={itemsList}
                loading={loading}
                ref={similarListRef}
                isStopLoad={data && !data.itemsConnection.pageInfo.hasNextPage}
                onLoadMore={() => {
                  const moreSimilarItemsQueryVariables = {
                    ...similarItemsQueryVariables,
                    cursor: data?.itemsConnection.pageInfo.endCursor
                  };
                  fetchMore({
                    variables: moreSimilarItemsQueryVariables
                  });
                }}
              >
                {itemsList}
              </EndlessFeaturedItems>
            </SimilarAdsList>
          )}
        </SpaceSection>
      </SimilarAdsContent>
    </SimilarAdsContainer>
  );
};

export const SimilarAdsSection = connect(
  null,
  mapDispatchToProps
)(SimilarAdsSectionComponent);
